import { RefreshCw, Save, Network } from "lucide-react";
import { PageHeader } from "../PageHeader";
import { MetricCard } from "../shared/MetricCard";
import { EventRow } from "../shared/EventRow";
import type { Severity } from "../shared/EventRow";
import { VerdictVolumeChart } from "../shared/VerdictVolumeChart";
import { MemoryMiniGraph } from "../shared/MemoryMiniGraph";
import {
  securityRoomStore,
  selectOpenApprovalsCount,
  selectBlocks24hCount,
  selectDlpHits24hCount,
  selectActiveSessionsCount,
  selectCriticalNow,
  selectPendingApprovals,
  selectHourlyVerdicts24h,
  selectBlocksList,
  selectHighRiskSessions,
} from "../../../stores/securityRoomStore";

function exportSnapshotJson(snap: unknown) {
  const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `security-room-${new Date().toISOString()}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function firstString(item: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "string" && v) return v;
  }
  return undefined;
}

function pickSeverity(item: Record<string, unknown>): Severity {
  const sev = (item.severity ?? item.decision) as string | undefined;
  if (sev === "allow" || sev === "ask" || sev === "deny" || sev === "crit" || sev === "info") {
    return sev;
  }
  return "info";
}

function shorten(s: string | undefined, n = 22): string {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// The header shows a passive "Live" pill, not a Refresh button. The page is fed
// by a reactive Convex subscription, so there is nothing to re-fetch — and the
// old button called window.location.reload(), tearing down every live terminal
// and WebSocket in the app to "refresh" data that was already current.
export function OverviewPage() {
  const snap = securityRoomStore.useStore();
  const approvalsOpen = selectOpenApprovalsCount(snap);
  const blocks = selectBlocks24hCount(snap);
  const dlp = selectDlpHits24hCount(snap);
  const sessions = selectActiveSessionsCount(snap);
  // Match the selector cap to the 4 rows the feed renders below — the
  // default max is 3, so the .slice(0, 4) would never reach 4.
  const critical = selectCriticalNow(snap, 4);
  const pending = selectPendingApprovals(snap, 4);
  const buckets = selectHourlyVerdicts24h(snap);

  // ── Mini-graph inputs, derived reactively from the same store ──
  const blocksList = selectBlocksList(snap);
  const highRisk = selectHighRiskSessions(snap);
  const dlpHits = ((snap.dlpHits ?? snap.dlpEvents ?? []) as Array<Record<string, unknown>>) || [];
  const briefs =
    ((snap.prRisks ?? snap.securityRoomBriefs ?? []) as Array<Record<string, unknown>>) || [];

  const agentSet = new Set<string>();
  [...pending, ...blocksList, ...highRisk].forEach((e) => {
    const a = firstString(e as Record<string, unknown>, ["agent", "who"]);
    if (a) agentSet.add(a);
  });
  const denies = blocksList.map((e) => ({
    label: shorten(firstString(e, ["command", "cmd", "rule", "reason"]), 18),
    agent: firstString(e, ["agent"]),
  }));
  const asks = pending.map((a) => ({
    label: shorten(firstString(a as Record<string, unknown>, ["command", "cmd", "rule"]), 18),
    agent: firstString(a as Record<string, unknown>, ["agent"]),
  }));
  const dlpNodes = dlpHits.map((d) => ({
    label: shorten(firstString(d, ["patternId", "label", "type"]), 16),
  }));
  const prNodes = briefs.map((b) => ({
    label: shorten(firstString(b, ["prUrl", "summary", "title"]), 18),
    score: typeof b.score === "number" ? (b.score as number) : undefined,
  }));

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={`live · ${sessions} active sessions · last 24h`}
        actions={
          <>
            <span className="sr-live-pill" title="Updates live via Convex subscription">
              <RefreshCw size={14} aria-hidden="true" /> Live
            </span>
            <button type="button" className="sr-btn" onClick={() => exportSnapshotJson(snap)}>
              <Save size={14} /> Export
            </button>
          </>
        }
      />
      <div className="sr-page-body">
        <div className="sr-metric-grid">
          <MetricCard label="Open approvals" value={approvalsOpen} tone="ask" />
          <MetricCard label="Blocks (24h)" value={blocks} tone="deny" />
          <MetricCard label="DLP hits" value={dlp} />
          <MetricCard label="Active sessions" value={sessions} tone="allow" />
        </div>

        {/* Hero chart */}
        <div className="sr-card sr-card--chart">
          <VerdictVolumeChart buckets={buckets} />
        </div>

        {/* Graph + live triage, side by side */}
        <div className="sr-overview-split">
          <div className="sr-card sr-graph">
            <div className="sr-graph__head">
              <div className="sr-section-title sr-section-title--flush">
                <Network size={14} /> Security memory
              </div>
              <a className="sr-graph__more" href="#memory">
                View full graph →
              </a>
            </div>
            <MemoryMiniGraph
              agents={[...agentSet]}
              denies={denies}
              asks={asks}
              dlp={dlpNodes}
              pr={prNodes}
            />
          </div>

          <div className="sr-card sr-overview-feed">
            <div className="sr-section-title sr-section-title--flush">Critical · Now</div>
            {critical.length === 0 ? (
              <div className="sr-empty">No critical events in the last 24h.</div>
            ) : (
              critical.map((raw, idx) => {
                const e = raw as Record<string, unknown>;
                const id = typeof e.id === "string" ? e.id : `crit-${idx}`;
                const ts =
                  firstString(e, ["recordedAt", "lastSeenAt", "createdAt", "ts", "at"]) ?? "";
                const title =
                  firstString(e, ["title", "reason", "command", "type"]) ?? "Critical event";
                const who = firstString(e, ["agent", "who"]);
                const rule = firstString(e, ["rule", "ruleId"]);
                const cmd = firstString(e, ["command", "cmd"]);
                return (
                  <EventRow
                    key={id}
                    event={{ id, ts, severity: pickSeverity(e), title, who, rule, cmd }}
                  />
                );
              })
            )}

            <div className="sr-section-title sr-section-title--flush">Pending approvals</div>
            {pending.length === 0 ? (
              <div className="sr-empty">No pending approvals.</div>
            ) : (
              pending.map((raw, idx) => {
                const a = raw as Record<string, unknown>;
                const id = typeof a.id === "string" ? a.id : `appr-${idx}`;
                const ts =
                  firstString(a, ["recordedAt", "lastSeenAt", "createdAt", "ts", "at"]) ?? "";
                const agent = firstString(a, ["agent"]) ?? "";
                const repo = firstString(a, ["repo", "project"]) ?? "";
                const who = [agent, repo].filter(Boolean).join(" · ") || undefined;
                const rule = firstString(a, ["rule", "ruleId"]);
                const cmd = firstString(a, ["command", "cmd"]);
                return (
                  <EventRow
                    key={id}
                    event={{ id, ts, severity: "ask", title: "Approval requested", who, rule, cmd }}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}
